import { memo, useState } from "react";

import { Input } from "@buildpy/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@buildpy/ui/select";
import { UNIT_SUGGESTIONS } from "@buildpy/core/modules/template/trades";

/** 시드에 쓰인 단위 + 「식」. 목록에 없으면 직접 입력으로 넘어간다. */
const UNITS: readonly string[] = [...UNIT_SUGGESTIONS, "식"];
const CUSTOM = "__custom__";

interface Props {
  value: string | null;
  onChange: (value: string) => void;
  onSettled: () => void;
  disabled?: boolean;
}

// 목록 밖 값(수량칸 「3개」 가 채운 단위 등)은 언제 들어오든 직접 입력 칸으로 보여야 해서 value 에서 매 렌더 파생한다.
export const isCustomUnit = (value: string | null | undefined, editing: boolean): boolean =>
  editing || (!!value && !UNITS.includes(value));

const UnitCell = ({ value: raw, onChange, onSettled, disabled }: Props) => {
  // 저장본·템플릿이 null 을 흘려도 칸이 죽지 않게 빈 문자열로 받는다.
  const value: string = raw ?? "";
  const [editing, setEditing] = useState(false);
  const custom = isCustomUnit(value, editing);

  if (custom) {
    return (
      <Input
        aria-label="규격 단위 직접 입력"
        autoFocus={value.length === 0}
        className="phone:min-h-10"
        disabled={disabled}
        placeholder="단위"
        variant="cell"
        value={value}
        onBlur={() => {
          setEditing(false);
          onSettled();
        }}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setEditing(true)}
      />
    );
  }

  return (
    <Select
      disabled={disabled}
      value={value.length > 0 ? value : undefined}
      onValueChange={(next) => {
        if (next === CUSTOM) {
          onChange("");
          setEditing(true);
          return;
        }
        onChange(next);
        onSettled();
      }}>
      <SelectTrigger
        aria-label="규격 단위"
        className="h-8 min-h-8 phone:h-10 phone:min-h-10"
        size="sm"
        variant="ghost">
        {/* 단위 글자를 직접 넣어 서버 렌더·하이드레이션 전에도 칸이 비지 않게 한다 — 목록 단위는 값이 곧 라벨이다. */}
        <SelectValue placeholder="단위">{value.length > 0 ? value : undefined}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {UNITS.map((unit) => (
          <SelectItem key={unit} value={unit}>
            {unit}
          </SelectItem>
        ))}
        <SelectItem value={CUSTOM}>직접 입력…</SelectItem>
      </SelectContent>
    </Select>
  );
};

export default memo(UnitCell);
